import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { GitHubButton } from '@/components/auth/GitHubButton';
import { Turnstile } from '@/components/auth/Turnstile';
import { useAuth } from '@/contexts/AuthContext';

interface SignupPageProps {
  features?: {
    oauth?: boolean;
    google_oauth?: boolean;
    github_oauth?: boolean;
    password_login?: boolean;
  };
  branding?: {
    name?: string;
  };
  inviteSecret?: string | null;
}

export function SignupPage({
  features = {
    oauth: true,
    google_oauth: true,
    github_oauth: true,
    password_login: true,
  },
  branding = {
    name: 'doploy',
  },
  inviteSecret = null,
}: SignupPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [challengeQuestion, setChallengeQuestion] = useState<string | null>(null);
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const { signup } = useAuth();
  
  const turnstileSiteKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY || '0x4AAAAAACAXLVY6peIBHA7D';

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    
    if (!confirmPassword) {
      errors.confirm_password = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirm_password = 'Passwords do not match';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    // If challenge is shown, require an answer
    if (challengeQuestion && !challengeAnswer.trim()) {
      setError('Please answer the security question.');
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }

    setIsLoading(true);

    try {
      const result = await signup({
        email,
        password,
        confirm_password: confirmPassword,
        cf_turnstile_response: turnstileToken,
        challenge_answer: challengeAnswer || undefined,
      });

      if (!result.success) {
        // Check if backend is requesting a challenge question
        if (result.requires_challenge && result.challenge_question) {
          setChallengeQuestion(result.challenge_question);
          setError(result.error || 'Please answer the security question to continue.');
          setIsLoading(false);
          return;
        }

        if (result.errors) {
          setFieldErrors(result.errors);
        } else {
          setError(result.error || 'An error occurred. Please try again.');
        }
        setIsLoading(false);
        return;
      }

      // Success - navigation is handled by auth context
    } catch {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  // Handle invite secret from URL if needed
  void inviteSecret;

  return (
    <AuthLayout>
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Title */}
      <h1 className="text-xl font-semibold text-gray-900 mb-6">
        Sign up for {branding.name}
      </h1>

      {/* Signup Form */}
      {features.password_login && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) {
                  setFieldErrors(prev => ({ ...prev, email: '' }));
                }
              }}
              className={fieldErrors.email ? 'border-red-500' : ''}
              required
              autoComplete="email"
              autoFocus
            />
            {fieldErrors.email && (
              <p className="text-sm text-red-500">{fieldErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (fieldErrors.password) {
                  setFieldErrors(prev => ({ ...prev, password: '' }));
                }
              }}
              className={fieldErrors.password ? 'border-red-500' : ''}
              required
              autoComplete="new-password"
            />
            {fieldErrors.password && (
              <p className="text-sm text-red-500">{fieldErrors.password}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm">Confirm Password</Label>
            <Input
              id="confirm"
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.confirm_password) {
                  setFieldErrors(prev => ({ ...prev, confirm_password: '' }));
                }
              }}
              className={fieldErrors.confirm_password ? 'border-red-500' : ''}
              required
              autoComplete="new-password"
            />
            {fieldErrors.confirm_password && (
              <p className="text-sm text-red-500">{fieldErrors.confirm_password}</p>
            )}
          </div>

          {/* Security Challenge Question (shown for suspicious emails) */}
          {challengeQuestion && (
            <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <Label htmlFor="challenge" className="text-amber-800 font-medium">
                Security Question
              </Label>
              <p className="text-sm text-amber-700">{challengeQuestion}</p>
              <Input
                id="challenge"
                type="text"
                placeholder="Your answer"
                value={challengeAnswer}
                onChange={(e) => setChallengeAnswer(e.target.value)}
                className="bg-white"
                autoComplete="off"
              />
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full h-11" 
            disabled={isLoading || (!!turnstileSiteKey && !turnstileToken)}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Signing up...
              </>
            ) : (
              'Sign Up'
            )}
          </Button>
        </form>
      )}

      {/* Turnstile Widget */}
      {turnstileSiteKey && (
        <Turnstile
          siteKey={turnstileSiteKey}
          onSuccess={(token) => setTurnstileToken(token)}
          onError={() => {
            setTurnstileToken(null);
            setError('Verification failed. Please try again.');
          }}
          onExpire={() => {
            setTurnstileToken(null);
            setError('Verification expired. Please try again.');
          }}
        />
      )}

      {/* Divider */}
      {features.oauth && features.password_login && (
        <div className="relative my-6">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-muted-foreground">
            or continue with
          </span>
        </div>
      )}

      {/* OAuth Buttons */}
      {features.oauth && (
        <div className="space-y-3">
          {features.google_oauth && (
            <GoogleButton 
              text="Sign up with Google" 
              disabled={!!turnstileSiteKey && !turnstileToken}
              onClick={() => {
                if (turnstileSiteKey && !turnstileToken) {
                  setError('Please complete the verification challenge.');
                  return;
                }
                // Verify token with backend before OAuth redirect
                if (turnstileToken) {
                  fetch('/api/v1/turnstile/verify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ token: turnstileToken }),
                  })
                    .then(res => res.json())
                    .then(data => {
                      if (data.success) {
                        window.location.href = '/oauth/google';
                      } else {
                        setError('Verification failed. Please try again.');
                        setTurnstileToken(null);
                      }
                    })
                    .catch(() => {
                      setError('Verification failed. Please try again.');
                    });
                } else {
                  window.location.href = '/oauth/google';
                }
              }}
            />
          )}
          {features.github_oauth && (
            <GitHubButton 
              text="Sign up with GitHub" 
              disabled={!!turnstileSiteKey && !turnstileToken}
              onClick={() => {
                if (turnstileSiteKey && !turnstileToken) {
                  setError('Please complete the verification challenge.');
                  return;
                }
                // Verify token with backend before OAuth redirect
                if (turnstileToken) {
                  fetch('/api/v1/turnstile/verify', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ token: turnstileToken }),
                  })
                    .then(res => res.json())
                    .then(data => {
                      if (data.success) {
                        window.location.href = '/oauth/github';
                      } else {
                        setError('Verification failed. Please try again.');
                        setTurnstileToken(null);
                      }
                    })
                    .catch(() => {
                      setError('Verification failed. Please try again.');
                    });
                } else {
                  window.location.href = '/oauth/github';
                }
              }}
            />
          )}
        </div>
      )}

      {/* Login Link */}
      <div className="mt-6 text-center">
        <Link 
          to="/auth/login" 
          className="text-sm text-muted-foreground hover:text-primary"
        >
          Already have an account?
        </Link>
      </div>
    </AuthLayout>
  );
}
